import React, { useEffect, useState } from "react"
import { Button, Card, Collapse, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { toast } from "react-toastify"

import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"

import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  HasModulePermission,
  useHandleUnauthorizedAccess,
} from "../../../lib/GlobalMethods"
import { modulePermission } from "../../../lib/GlobalVariables"
import useDebounce from "../../../lib/hooks/useDebounce"

import AddEditFleetClient from "./AddEditFleetClient"
import FleetClientManagementSkeleton from "../../../skeleton/fleetManagement/fleetClientManagement"

const FleetClientManagement = () => {
  const canAdd = HasModulePermission(modulePermission.fleetManagement, "add")

  const canEdit = HasModulePermission(modulePermission.fleetManagement, "edit")

  const canDelete = HasModulePermission(
    modulePermission.fleetManagement,
    "delete",
  )

  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [loading, setLoading] = useState(true)
  const [archived, setArchived] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })

  const [recordsCount, setRecordsCount] = useState({
    total_records: 0,
    active_records: 0,
    archived_records: 0,
  })

  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)

  const [filterObj, setFilterObj] = useState({
    active: false,
    archived: false,
  })

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleShow = () => {
    setIsUpdate(false)
    setShow(true)
  }

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
  }

  const fetchData = async (search = debouncedSearchQuery) => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.fleetClientList}?page=${
          pagination.current_page
        }&limit=10&search_term=${encodeURIComponent(
          search || "",
        )}&archived=${filterObj.archived}&active=${filterObj.active}`,
      })

      handleAccess({
        code: response?.data?.code,
      })

      if (response?.data?.code === 200) {
        const fetchedData = response?.data?.data || []

        setPagination(response?.data?.pagination)

        let recordCounts = response?.data?.record_counts

        if (!fetchedData.length && (filterObj.active || filterObj.archived)) {
          const countResponse = await axiosProvider({
            method: "GET",
            endpoint: `${admin.fleetClientList}?page=1&limit=1&search_term=\
&archived=false&active=false`,
          })
          recordCounts = countResponse?.data?.record_counts || recordCounts
        }

        setRecordsCount(
          recordCounts || {
            total_records: 0,
            active_records: 0,
            archived_records: 0,
          },
        )

        if (
          !fetchedData.length ||
          response?.data?.pagination?.current_page ===
            response?.data?.pagination?.total_pages
        ) {
          setHasMore(false)
        } else {
          setHasMore(true)
        }

        setData((prevData) =>
          pagination.current_page === 1
            ? fetchedData
            : [...prevData, ...fetchedData],
        )
      }
    } catch (error) {
      toast.error(error?.message || "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, filterObj, pagination.current_page, refreshKey])

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const handleArchiveRestore = async (id) => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: "DELETE",
        endpoint: archived
          ? admin.restoreFleetClient + id
          : admin.deleteFleetClient + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        handleClose()

        setData([])
        if (archived) {
          setFilterObj({
            active: false,
            archived: false,
          })
        }
        setPagination({
          current_page: 1,
          total_pages: false,
        })
        setRefreshKey((previousKey) => previousKey + 1)
      } else {
        throw new Error(response?.data?.message)
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
      setLoading(false)
    }
  }

  const resetAndSetFilter = (filter) => {
    setFilterObj(filter)
    setPagination({
      current_page: 1,
      total_pages: false,
    })
    setLoading(true)
  }

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText="Fleet Client Management"
          HeaderButtonText="Add Fleet Client"
          IconclassName="plus_black"
          onClick={handleShow}
          filterShow={true}
          filterClick={() => setOpen(!open)}
          open={open}
        />
      ) : (
        <PageTitleButton
          PagetitleText="Fleet Client Management"
          filterShow={true}
          filterClick={() => setOpen(!open)}
          open={open}
        />
      )}

      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            !filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() =>
            resetAndSetFilter({
              active: false,
              archived: false,
            })
          }
        >
          <div className="card_titleicon">
            <h3 className="card_title">Total Fleet Clients</h3>
          </div>

          <p className="tls_numbers">{recordsCount.total_records || 0}</p>
        </div>

        <div
          className={`bg_light_green details_card ${
            filterObj.active ? "card_active" : ""
          }`}
          onClick={() =>
            resetAndSetFilter({
              active: true,
              archived: false,
            })
          }
        >
          <div className="card_titleicon">
            <h3 className="card_title">Active Fleet Clients</h3>
          </div>

          <p className="tls_numbers">{recordsCount.active_records || 0}</p>
        </div>

        <div
          className={`bg_light_red details_card ${
            filterObj.archived ? "card_active" : ""
          }`}
          onClick={() =>
            resetAndSetFilter({
              active: false,
              archived: true,
            })
          }
        >
          <div className="card_titleicon">
            <h3 className="card_title">Archived Fleet Clients</h3>
          </div>

          <p className="tls_numbers">{recordsCount.archived_records || 0}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="fleet-client-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <SearchBox
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e?.target?.value)
                  setPagination({
                    current_page: 1,
                    total_pages: false,
                  })
                  setLoading(true)
                }}
              />
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 0}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<FleetClientManagementSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th>Client ID</th>
                    <th>Client Name</th>
                    <th>Assigned Stations</th>
                    <th>Status</th>

                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {data?.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>

                        <td>{item.name}</td>

                        <td>
                          {item?.stations?.length > 0
                            ? item.stations
                                .map((station) => station?.station_name)
                                .join(", ")
                            : "No stations assigned"}
                        </td>

                        <td>
                          {item?.deletedAt
                            ? "Archived"
                            : item?.status || "Active"}
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit && !item?.deletedAt && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Edit"
                                >
                                  <Button
                                    variant="btnedit_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setIsUpdate(item)
                                      setShow(true)
                                    }}
                                  >
                                    <i className="icon_main edit_pencil"></i>
                                  </Button>
                                </TooltipComponent>
                              )}

                              {canDelete && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText={
                                    item?.deletedAt ? "Restore" : "Archive"
                                  }
                                >
                                  <Button
                                    variant={
                                      item?.deletedAt
                                        ? "btnrestore_icon"
                                        : "btnarchive_icon"
                                    }
                                    className="btn_icon"
                                    onClick={() => {
                                      setIsUpdate(item)
                                      setArchived(Boolean(item?.deletedAt))
                                      setShowAlert(true)
                                    }}
                                  >
                                    <i
                                      className={`icon_main ${
                                        item?.deletedAt
                                          ? "restore_data"
                                          : "archive_data"
                                      }`}
                                    ></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="text-center py-4"
                        colSpan={canEdit || canDelete ? 5 : 4}
                      >
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <FleetClientManagementSkeleton />
      )}

      <AddEditFleetClient
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={() => {
          setPagination({
            current_page: 1,
            total_pages: false,
          })
          fetchData("")
        }}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
      />

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={`Are you sure you want to ${
          archived ? "Restore" : "Archive"
        } this Fleet Client?`}
        handleClose={handleClose}
        iconClassName={
          archived ? "restore_data bg-black" : "archive_data bg-warning"
        }
        handleAlert={() => handleArchiveRestore(isUpdate?.id)}
      />
    </>
  )
}

export default FleetClientManagement
